import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Smartphone, Dumbbell, Heart, Zap, DollarSign, Users, Star } from 'lucide-react';
import ShareResult from '../ShareResult';

const QUIZ_QUESTIONS = [
  {
    id: 'goal',
    question: 'What\'s your primary fitness goal?',
    options: [
      { label: 'General wellness (workout + nutrition + mindfulness)', tags: ['hoola', 'centr'] },
      { label: 'Strength / muscle building', tags: ['fitbod', 'jefit'] },
      { label: 'Cardio / HIIT / fat loss', tags: ['freeletics', 'aaptiv'] },
      { label: 'Studio-style classes at home', tags: ['peloton', 'fiton', 'ntc'] },
    ],
  },
  {
    id: 'equipment',
    question: 'What equipment do you have?',
    options: [
      { label: 'Full gym access', tags: ['fitbod', 'jefit', 'peloton'] },
      { label: 'Dumbbells / kettlebells at home', tags: ['hoola', 'fitbod', 'centr'] },
      { label: 'No equipment — bodyweight only', tags: ['freeletics', 'fiton', 'ntc'] },
      { label: 'Treadmill / bike / rower', tags: ['aaptiv', 'peloton'] },
    ],
  },
  {
    id: 'budget',
    question: 'Monthly budget for a fitness app?',
    options: [
      { label: 'Free', tags: ['fiton', 'ntc'] },
      { label: '$5-15/month', tags: ['peloton', 'aaptiv', 'centr'] },
      { label: '$15-30/month', tags: ['hoola', 'freeletics'] },
      { label: 'Willing to invest more', tags: ['fitbod', 'jefit', 'hoola'] },
    ],
  },
  {
    id: 'style',
    question: 'What motivates you most?',
    options: [
      { label: 'Data and tracking (reps, sets, PRs)', tags: ['fitbod', 'jefit'] },
      { label: 'Leaderboards and gamification', tags: ['freeletics', 'peloton'] },
      { label: 'Trainer-led video classes', tags: ['centr', 'peloton', 'fiton'] },
      { label: 'AI that adapts to me automatically', tags: ['hoola', 'fitbod', 'freeletics'] },
    ],
  },
];

const APPS = {
  hoola: { name: 'Hoola', category: 'All-in-One Wellness', price: '$30/mo', rating: 4.7, color: '#818cf8' },
  centr: { name: 'Centr', category: 'All-in-One Wellness', price: '$100/yr', rating: 4.6, color: '#22c55e' },
  fitbod: { name: 'Fitbod', category: 'Strength Specialist', price: '$96/yr', rating: 4.8, color: '#E63946' },
  jefit: { name: 'JEFIT', category: 'Strength / Tracking', price: '$70/yr', rating: 4.8, color: '#f59e0b' },
  freeletics: { name: 'Freeletics', category: 'Bodyweight HIIT', price: '$35/qtr', rating: 4.7, color: '#00F0FF' },
  aaptiv: { name: 'Aaptiv', category: 'Audio Coaching', price: '$15/mo', rating: 4.6, color: '#a78bfa' },
  peloton: { name: 'Peloton App', category: 'Studio Classes', price: '$10-20/mo', rating: 4.7, color: '#E63946' },
  fiton: { name: 'FitOn', category: 'Free Classes', price: 'Free', rating: 4.8, color: '#22c55e' },
  ntc: { name: 'Nike Training Club', category: 'Free Programs', price: 'Free', rating: 4.7, color: '#f59e0b' },
};

export default function AIFitnessAppsArticle() {
  const [quizStep, setQuizStep] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);

  const handleAnswer = (qId, tags) => {
    const updated = { ...quizAnswers, [qId]: tags };
    setQuizAnswers(updated);
    if (quizStep < QUIZ_QUESTIONS.length - 1) {
      setTimeout(() => setQuizStep(s => s + 1), 200);
    } else {
      setTimeout(() => setShowResult(true), 200);
    }
  };

  // Calculate results
  const scores = {};
  Object.values(quizAnswers).forEach(tags => {
    tags.forEach(t => { scores[t] = (scores[t] || 0) + 1; });
  });
  const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]).slice(0, 3);

  const resetQuiz = () => { setQuizStep(0); setQuizAnswers({}); setShowResult(false); };

  return (
    <main className="pt-20 min-h-screen bg-[#0a0a0a]" data-testid="ai-fitness-apps-article">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/blog" className="text-zinc-500 text-xs uppercase tracking-wider hover:text-white transition-colors mb-4 inline-block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Back to Blog</Link>

        <h1 className="text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="ai-apps-title">9 AI-Powered Fitness Apps That Personalize Your Workouts</h1>
        <p className="text-zinc-400 text-lg mb-8 leading-relaxed">Generic plans stall out by week three. Adaptive AI reverses that trend — here's how to find the right app for you.</p>

        {/* Interactive: App Finder Quiz */}
        <div className="border border-[#818cf8]/30 bg-[#818cf8]/5 p-6 mb-10" data-testid="app-finder-quiz">
          <div className="flex items-center gap-2 mb-1">
            <Smartphone className="w-5 h-5 text-[#818cf8]" />
            <h3 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Find Your Perfect App</h3>
          </div>
          <p className="text-zinc-500 text-xs mb-5">Answer 4 questions and we'll match you to the best AI fitness app.</p>

          {!showResult ? (
            <>
              {/* Progress */}
              <div className="flex gap-1 mb-4">
                {QUIZ_QUESTIONS.map((q, i) => (
                  <div key={q.id} className={`flex-1 h-1 ${i <= quizStep ? 'bg-[#818cf8]' : 'bg-zinc-800'} transition-all`} />
                ))}
              </div>

              <p className="text-white text-sm mb-4">{QUIZ_QUESTIONS[quizStep].question}</p>
              <div className="space-y-2">
                {QUIZ_QUESTIONS[quizStep].options.map(opt => {
                  const selected = (quizAnswers[QUIZ_QUESTIONS[quizStep].id] || []).join() === opt.tags.join();
                  return (
                    <button key={opt.label}
                      onClick={() => handleAnswer(QUIZ_QUESTIONS[quizStep].id, opt.tags)}
                      className={`w-full text-left px-4 py-3 border transition-all text-sm ${selected ? 'border-[#818cf8]/60 bg-[#818cf8]/10 text-white' : 'border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-white'}`}
                      data-testid={`quiz-opt-${QUIZ_QUESTIONS[quizStep].id}-${opt.label.slice(0, 15).replace(/\s/g, '-').toLowerCase()}`}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {quizStep > 0 && (
                <button onClick={() => setQuizStep(s => s - 1)} className="mt-3 text-zinc-500 text-xs hover:text-white transition-colors">Back</button>
              )}
            </>
          ) : (
            <div data-testid="app-quiz-results">
              <p className="text-white text-sm font-bold mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Your Top Matches</p>
              <div className="space-y-3">
                {ranked.map(([appId, matchScore], i) => {
                  const app = APPS[appId];
                  if (!app) return null;
                  return (
                    <div key={appId} className="flex items-center gap-4 p-4 border border-zinc-800 bg-zinc-900/40" data-testid={`match-${appId}`}>
                      <span className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif', color: app.color }}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm font-bold">{app.name}</span>
                          <span className="text-[9px] px-1.5 py-0.5 border border-zinc-700 text-zinc-500">{app.category}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-500">
                          <span className="flex items-center gap-1"><Star className="w-3 h-3 text-[#f59e0b]" /> {app.rating}</span>
                          <span>{app.price}</span>
                          <span className="text-[#818cf8]">{matchScore}/{QUIZ_QUESTIONS.length} match</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button onClick={resetQuiz} className="mt-4 px-4 py-2 border border-zinc-700 text-zinc-400 text-xs uppercase tracking-wider hover:text-white transition-colors" data-testid="quiz-retake">Retake Quiz</button>
              {ranked.length > 0 && ranked[0][0] && APPS[ranked[0][0]] && (
                <div className="mt-3">
                  <ShareResult
                    text={`My top AI fitness app match is ${APPS[ranked[0][0]].name}! Find your perfect fitness app:`}
                    url="https://ninjathlete.com/blogs/article/ai-powered-fitness-apps"
                    label="Share My Match"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Article Content */}
        <div className="prose-ninja space-y-6 text-zinc-300 leading-relaxed mb-10">
          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>How to Spot Genuine Personalization</h2>
          <ul className="space-y-2">
            <li><strong className="text-white">Responsive feedback</strong> — If you tap "too hard," tomorrow's load should ease.</li>
            <li><strong className="text-white">Multiple data feeds</strong> — Best apps blend workout logs, HRV, sleep, and stress.</li>
            <li><strong className="text-white">Visible logic</strong> — Shows why it trimmed reps or bumped weight.</li>
          </ul>

          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Quick Decision Guide</h2>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'All-in-One Wellness', apps: 'Hoola, Centr', color: '#818cf8' },
              { label: 'Strength & Lifting', apps: 'Fitbod, JEFIT', color: '#E63946' },
              { label: 'Equipment-Free HIIT', apps: 'Freeletics, Aaptiv', color: '#00F0FF' },
              { label: 'Studio Energy', apps: 'Peloton, FitOn, NTC', color: '#f59e0b' },
            ].map(g => (
              <div key={g.label} className="border border-zinc-800 p-3">
                <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: g.color }}>{g.label}</span>
                <p className="text-white text-sm mt-1">{g.apps}</p>
              </div>
            ))}
          </div>

          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Benefits of AI Coaching</h2>
          <div className="grid grid-cols-2 gap-3">
            {[
              { stat: '$8-15/mo', desc: 'vs $55/hr for personal trainers', icon: DollarSign },
              { stat: '18%', desc: 'higher training intensity', icon: Zap },
              { stat: '57%', desc: 'more likely to finish workouts', icon: Heart },
              { stat: 'Unified', desc: 'sync workouts, sleep, HRV, nutrition', icon: Users },
            ].map(s => (
              <div key={s.stat} className="bg-zinc-900/40 border border-zinc-800 p-3 flex items-center gap-3">
                <s.icon className="w-5 h-5 text-[#818cf8] flex-shrink-0" />
                <div>
                  <p className="text-white text-sm font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{s.stat}</p>
                  <p className="text-zinc-500 text-[10px]">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
